import type {
  CompanyPaymentMethod,
  CompanyPaymentMethodInput,
  Expense,
  ExpenseCategory,
  ExpenseCategoryInput,
  ExpenseInput,
  ExpenseListItem,
  ExpenseSummary,
  ExpenseSummaryQuery,
  ExpenseUpdate,
  Page,
  PendingRecurringOccurrence,
  RecurringExpense,
  RecurringExpenseInput,
  RecurringExpenseListItem,
  RecurringExpenseUpdate,
  RecurringOccurrencePost,
  TaxRate,
  TaxRateInput,
  TreasuryAccount,
  TreasuryAccountCreate,
  TreasuryAccountRecord,
  TreasuryAccountUpdate,
  TreasuryTransfer,
  TreasuryTransferInput,
} from '@sahab/contracts'
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { invalidateLedger, ledgerKeys } from '../accounting/queries'
import type { ExpenseListParams, RecurringListParams, TransferListParams } from './search'

export const financeKeys = {
  treasury: ['treasury-accounts'] as const,
  transfers: ['treasury-transfers'] as const,
  transferList: (query: TransferListParams) => [...financeKeys.transfers, 'list', query] as const,
  categories: ['expense-categories'] as const,
  taxRates: ['tax-rates'] as const,
  paymentMethods: ['payment-methods'] as const,
  paymentMethodList: (includeArchived: boolean) =>
    [...financeKeys.paymentMethods, includeArchived ? 'all' : 'active'] as const,
  expenses: ['expenses'] as const,
  expenseList: (query: ExpenseListParams) => [...financeKeys.expenses, 'list', query] as const,
  expenseSummary: (query: ExpenseSummaryQuery) => [...financeKeys.expenses, 'summary', query] as const,
  expense: (id: string) => [...financeKeys.expenses, 'detail', id] as const,
  recurring: ['recurring-expenses'] as const,
  recurringList: (query: RecurringListParams) => [...financeKeys.recurring, 'list', query] as const,
  recurringPending: ['recurring-expenses', 'pending'] as const,
  recurringOne: (id: string) => [...financeKeys.recurring, 'detail', id] as const,
}

const path = (base: string, id: string, action?: string) =>
  `${base}/${encodeURIComponent(id)}${action ? `/${action}` : ''}`

// ── treasury ─────────────────────────────────────────────────────────────────

export const treasuryAccountsQueryOptions = queryOptions({
  queryKey: financeKeys.treasury,
  queryFn: ({ signal }) => api.get<TreasuryAccount[]>('/treasury-accounts', { signal }),
})

export function transferListQueryOptions(query: TransferListParams) {
  return queryOptions({
    queryKey: financeKeys.transferList(query),
    queryFn: ({ signal }) =>
      api.get<Page<TreasuryTransfer>>('/treasury-transfers', {
        signal,
        query: { page: query.page, pageSize: query.pageSize },
      }),
    placeholderData: keepPreviousData,
  })
}

/**
 * Active accounts' master data without balances (`settings.manage` or `treasury.manage`): Settings ›
 * Payment methods maintains them when Cash & bank is out of reach (ADR-024 D2).
 */
export const treasuryRecordsQueryOptions = queryOptions({
  queryKey: [...financeKeys.treasury, 'setup'] as const,
  queryFn: ({ signal }) => api.get<TreasuryAccountRecord[]>('/treasury-accounts/setup', { signal }),
})

export function useSaveTreasuryAccount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string | null
      input: TreasuryAccountCreate | TreasuryAccountUpdate
    }) =>
      id
        ? api.put<TreasuryAccountRecord>(path('/treasury-accounts', id), input)
        : api.post<TreasuryAccountRecord>('/treasury-accounts', input),
    onSuccess: () =>
      Promise.all([
        // A new account gets its own ledger account (and maybe an opening entry).
        queryClient.invalidateQueries({ queryKey: ledgerKeys.accounts }),
        invalidateLedger(queryClient),
      ]),
  })
}

export function useArchiveTreasuryAccount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<void>(path('/treasury-accounts', id, 'archive')),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: financeKeys.treasury }),
  })
}

export function useCreateTransfer() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: TreasuryTransferInput) => api.post<TreasuryTransfer>('/treasury-transfers', input),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: financeKeys.transfers }),
        invalidateLedger(queryClient),
      ]),
  })
}

export function useVoidTransfer() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post<TreasuryTransfer>(path('/treasury-transfers', id, 'void'), { reason }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: financeKeys.transfers }),
        invalidateLedger(queryClient),
      ]),
  })
}

// ── tax rates ────────────────────────────────────────────────────────────────

export const taxRatesQueryOptions = queryOptions({
  queryKey: financeKeys.taxRates,
  queryFn: ({ signal }) => api.get<TaxRate[]>('/tax-rates', { signal }),
  staleTime: 5 * 60_000,
})

export function useSaveTaxRate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: TaxRateInput & { version?: number } }) =>
      id ? api.put<TaxRate>(path('/tax-rates', id), input) : api.post<TaxRate>('/tax-rates', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: financeKeys.taxRates }),
  })
}

export function useArchiveTaxRate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<void>(path('/tax-rates', id, 'archive')),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: financeKeys.taxRates }),
  })
}

// ── company payment methods (ADR-047) ────────────────────────────────────────

/**
 * The company's own payment methods (Visa, Thawani, Bank Muscat transfer), by type then name: the
 * active ones for every payment form and the till, with the archived ones for Settings › Payment methods.
 */
export function paymentMethodsQueryOptions(includeArchived = false) {
  return queryOptions({
    queryKey: financeKeys.paymentMethodList(includeArchived),
    queryFn: ({ signal }) =>
      api.get<CompanyPaymentMethod[]>('/payment-methods', {
        signal,
        query: includeArchived ? { includeArchived: 'true' } : {},
      }),
    staleTime: 5 * 60_000,
  })
}

export function useSavePaymentMethod() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string | null
      input: CompanyPaymentMethodInput & { version?: number }
    }) =>
      id
        ? api.put<CompanyPaymentMethod>(path('/payment-methods', id), input)
        : api.post<CompanyPaymentMethod>('/payment-methods', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: financeKeys.paymentMethods }),
  })
}

/** Archive (`archive: true`) or restore a company payment method. */
export function useArchivePaymentMethod() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, archive }: { id: string; archive: boolean }) =>
      api.post<CompanyPaymentMethod>(path('/payment-methods', id, archive ? 'archive' : 'restore')),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: financeKeys.paymentMethods }),
  })
}

// ── expense categories ───────────────────────────────────────────────────────

export const expenseCategoriesQueryOptions = queryOptions({
  queryKey: financeKeys.categories,
  queryFn: ({ signal }) => api.get<ExpenseCategory[]>('/expense-categories', { signal }),
  staleTime: 60_000,
})

export function useSaveExpenseCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: ExpenseCategoryInput & { version?: number } }) =>
      id
        ? api.put<ExpenseCategory>(path('/expense-categories', id), input)
        : api.post<ExpenseCategory>('/expense-categories', input),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: financeKeys.categories }),
        // Category names appear on expense rows.
        queryClient.invalidateQueries({ queryKey: financeKeys.expenses }),
      ]),
  })
}

export function useArchiveExpenseCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<void>(path('/expense-categories', id, 'archive')),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: financeKeys.categories }),
  })
}

// ── expenses ─────────────────────────────────────────────────────────────────

export function expenseListQueryOptions(query: ExpenseListParams) {
  return queryOptions({
    queryKey: financeKeys.expenseList(query),
    queryFn: ({ signal }) =>
      api.get<Page<ExpenseListItem>>('/expenses', {
        signal,
        query: {
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort,
          q: query.q,
          from: query.from,
          to: query.to,
          categoryId: query.categoryId,
          treasuryAccountId: query.treasuryAccountId,
          status: query.status,
          projectId: query.projectId,
        },
      }),
    placeholderData: keepPreviousData,
  })
}

/** Counts per status, posted totals and the monthly trend for the list's filters other than the status. */
export function expenseSummaryQueryOptions(list: ExpenseListParams) {
  const query: ExpenseSummaryQuery = {
    q: list.q,
    from: list.from,
    to: list.to,
    categoryId: list.categoryId,
    treasuryAccountId: list.treasuryAccountId,
    projectId: list.projectId,
  }
  return queryOptions({
    queryKey: financeKeys.expenseSummary(query),
    queryFn: ({ signal }) => api.get<ExpenseSummary>('/expenses/summary', { signal, query: { ...query } }),
    placeholderData: keepPreviousData,
  })
}

export function expenseQueryOptions(id: string) {
  return queryOptions({
    queryKey: financeKeys.expense(id),
    queryFn: ({ signal }) => api.get<Expense>(path('/expenses', id), { signal }),
  })
}

function useExpenseMutation<TInput>(request: (input: TInput) => Promise<Expense>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: request,
    onSuccess: (expense) => {
      queryClient.setQueryData(financeKeys.expense(expense.id), expense)
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: [...financeKeys.expenses, 'list'] }),
        invalidateLedger(queryClient),
      ])
    },
  })
}

export function useCreateExpense() {
  return useExpenseMutation((input: ExpenseInput) => api.post<Expense>('/expenses', input))
}

export function useUpdateExpense(id: string) {
  return useExpenseMutation((input: ExpenseUpdate) => api.put<Expense>(path('/expenses', id), input))
}

export function useVoidExpense(id: string) {
  return useExpenseMutation((reason: string) => api.post<Expense>(path('/expenses', id, 'void'), { reason }))
}

// ── recurring expenses (ADR-043) ─────────────────────────────────────────────

export function recurringListQueryOptions(query: RecurringListParams) {
  return queryOptions({
    queryKey: financeKeys.recurringList(query),
    queryFn: ({ signal }) =>
      api.get<Page<RecurringExpenseListItem>>('/recurring-expenses', {
        signal,
        query: {
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort,
          q: query.q,
          status: query.status,
        },
      }),
    placeholderData: keepPreviousData,
  })
}

/** Occurrences waiting for review, across every recurring expense. */
export const recurringPendingQueryOptions = queryOptions({
  queryKey: financeKeys.recurringPending,
  queryFn: ({ signal }) =>
    api.get<{ items: PendingRecurringOccurrence[]; total: number }>('/recurring-expenses/pending', {
      signal,
    }),
})

export function recurringQueryOptions(id: string) {
  return queryOptions({
    queryKey: financeKeys.recurringOne(id),
    queryFn: ({ signal }) => api.get<RecurringExpense>(path('/recurring-expenses', id), { signal }),
  })
}

/**
 * Saving or running a schedule may create expenses, and saving one from an expense links that expense
 * ("Recurring: …"): the expense lists and pages and the books refresh too.
 */
function useRecurringMutation<TInput>(request: (input: TInput) => Promise<RecurringExpense>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: request,
    onSuccess: (recurring) => {
      queryClient.setQueryData(financeKeys.recurringOne(recurring.id), recurring)
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: [...financeKeys.recurring, 'list'] }),
        queryClient.invalidateQueries({ queryKey: financeKeys.recurringPending }),
        queryClient.invalidateQueries({ queryKey: [...financeKeys.expenses, 'list'] }),
        queryClient.invalidateQueries({ queryKey: [...financeKeys.expenses, 'detail'] }),
        invalidateLedger(queryClient),
      ])
    },
  })
}

export function useCreateRecurring() {
  return useRecurringMutation((input: RecurringExpenseInput) =>
    api.post<RecurringExpense>('/recurring-expenses', input),
  )
}

export function useUpdateRecurring(id: string) {
  return useRecurringMutation((input: RecurringExpenseUpdate) =>
    api.put<RecurringExpense>(path('/recurring-expenses', id), input),
  )
}

export type RecurringCommand = 'pause' | 'resume' | 'end' | 'run'

export function useRecurringCommand(id: string) {
  return useRecurringMutation((command: RecurringCommand) =>
    api.post<RecurringExpense>(path('/recurring-expenses', id, command)),
  )
}

export function useSkipOccurrence(id: string) {
  return useRecurringMutation((occurrenceId: string) =>
    api.post<RecurringExpense>(
      `${path('/recurring-expenses', id)}/occurrences/${encodeURIComponent(occurrenceId)}/skip`,
    ),
  )
}

export function usePostOccurrence(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ occurrenceId, input }: { occurrenceId: string; input: RecurringOccurrencePost }) =>
      api.post<Expense>(
        `${path('/recurring-expenses', id)}/occurrences/${encodeURIComponent(occurrenceId)}/post`,
        input,
      ),
    onSuccess: (expense) => {
      queryClient.setQueryData(financeKeys.expense(expense.id), expense)
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: financeKeys.recurring }),
        queryClient.invalidateQueries({ queryKey: [...financeKeys.expenses, 'list'] }),
        invalidateLedger(queryClient),
      ])
    },
  })
}

export function useDeleteRecurring() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(path('/recurring-expenses', id)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...financeKeys.recurring, 'list'] }),
  })
}
